import { fieldConfig } from "@autoform/zod";
import * as z from "zod";
import { CAPTURE_ANGLES } from "../common/enums";
import { zNumber } from "../common/schemaHelpers";

// ---------------------------------------------------------------------------
// Pages Config — Flow Pages
// ---------------------------------------------------------------------------

const fromHomePages = z
	.array(
		z.enum([
			"home",
			"quote-vs-estimate",
			"device-role-instructions",
			"select-scanner-device",
			"prepare-scanner-device",
			"imei-instructions",
			"imei-scan",
			"max-price",
			"instructions",
			"device-pairing",
			"image-capture",
			"device-grading",
			"offer",
			"webapp-assessment",
			"user-info",
			"redirection",
			"process-completed",
		]),
	)
	.default([
		"home",
		"device-role-instructions",
		"select-scanner-device",
		"prepare-scanner-device",
		"imei-instructions",
		"imei-scan",
		"instructions",
		"device-pairing",
		"image-capture",
		"device-grading",
		"webapp-assessment",
	])
	.check(
		fieldConfig({
			fieldType: "multiselect",
			description: "Select which pages are enabled. Order follows the app's default sequence.",
		}),
	)
	.describe("From-Home Flow Pages");

const fromStorePages = z
	.array(
		z.enum([
			"home",
			"imei-instructions",
			"imei-scan",
			"max-price",
			"instructions",
			"device-pairing",
			"image-capture",
			"device-grading",
			"offer",
			"webapp-assessment",
			"user-info",
			"redirection",
			"payment-methods",
			"process-completed",
		]),
	)
	.default([
		"home",
		"imei-instructions",
		"imei-scan",
		"instructions",
		"device-pairing",
		"image-capture",
		"device-grading",
		"webapp-assessment",
	])
	.check(
		fieldConfig({
			fieldType: "multiselect",
			description: "Select which pages are enabled. Order follows the app's default sequence.",
		}),
	)
	.describe("From-Store Flow Pages");

// ---------------------------------------------------------------------------
// Pages Config — Home Screen
// ---------------------------------------------------------------------------

const carouselSchema = z.object({
	images: z.string().optional().describe("Image URLs"),
	delay: zNumber().optional().describe("Image Switch Delay (ms)"),
});

const homeSchema = z
	.object({
		mode: z
			.enum(["default", "video", "carousel"])
			.default("default")
			.check(
				fieldConfig({
					fieldType: "singleselect",
					description: "Select how the home screen content is rendered",
				}),
			)
			.describe("Home Screen Mode"),
		title: z.string().default("Trade in your device for instant value.").describe("Home Screen Title Key"),
		videoUrl: z
			.string()
			.optional()
			.check(
				fieldConfig({
					fieldType: "url",
					description: "Required only in video mode",
				}),
			)
			.describe("Video URL"),
		carousel: carouselSchema.optional().describe("Carousel Settings"),
		hasSecurePin: z.boolean().default(false).describe("Secure Pin"),
	})
	.refine((val) => val.mode !== "video" || (val.videoUrl !== undefined && val.videoUrl !== ""), {
		message: 'Video URL is required when mode is "video"',
		path: ["videoUrl"],
	})
	.refine((val) => val.mode !== "carousel" || val.carousel !== undefined, {
		message: 'Carousel settings are required when mode is "carousel"',
		path: ["carousel"],
	});

// ---------------------------------------------------------------------------
// Pages Config — IMEI Scan
// ---------------------------------------------------------------------------

const imeiScanSchema = z.object({
	cameraFacingMode: z
		.enum(["user", "environment"])
		.default("user")
		.check(
			fieldConfig({
				fieldType: "singleselect",
				description: "Select `user` for front-facing camera and `environment` for back camera",
			}),
		)
		.describe("Camera Facing Mode"),
});

// ---------------------------------------------------------------------------
// Pages Config — Offer Screen
// ---------------------------------------------------------------------------

const offerSchema = z.object({
	displayEstimatedPrice: z.boolean().default(true).describe("Display Estimated Price"),
	displayGradingReport: z.boolean().default(true).describe("Display Grading Report"),
});

// ---------------------------------------------------------------------------
// Pages Config — Image Capture
// ---------------------------------------------------------------------------

const imageCaptureSchema = z.object({
	captureFlow: z
		.enum(["DYNAMIC_PROGRESS_BAR", "BACK_CAMERA_MANUAL", "BACK_CAMERA_WEB_NATIVE"])
		.default("BACK_CAMERA_MANUAL")
		.check(fieldConfig({ fieldType: "singleselect" }))
		.describe("Capture Flow"),
	captureAngles: z
		.array(z.enum(CAPTURE_ANGLES))
		.default([])
		.check(
			fieldConfig({
				fieldType: "multiselect",
				description: "The angles we want to capture during the capture flow",
			}),
		)
		.describe("Capture Angles"),
	realtimeScreening: z
		.boolean()
		.default(false)
		.check(
			fieldConfig({
				description:
					"Screen the live camera feed during each manual pair-device capture and hold the shot until a frame is usable. Off keeps the legacy capture checks.",
			}),
		)
		.describe("Real-time Screening"),
});

// ---------------------------------------------------------------------------
// Pages Config — Instructions (device preparation steps)
// ---------------------------------------------------------------------------

const instructionsSchema = z.object({
	removeScreenProtector: z.boolean().default(true).describe("Show Remove Screen Protector Step"),
	removeAccessories: z.boolean().default(true).describe("Show Remove Accessories Step"),
	cleanDevice: z.boolean().default(true).describe("Show Clean Device Step"),
});

// ---------------------------------------------------------------------------
// Pages Config — User Info
// ---------------------------------------------------------------------------

const userInfoSchema = z.object({
	contactInfo: z.boolean().default(true).describe("Display Contact Info Section"),
	addressInfo: z.boolean().default(true).describe("Display Address Info Section"),
	idInfo: z.boolean().default(true).describe("Display ID Info Section"),
});

// ---------------------------------------------------------------------------
// Pages Config (top-level section)
// ---------------------------------------------------------------------------

const pagesConfigSchema = z.object({
	fromHome: fromHomePages,
	fromStore: fromStorePages,
	home: homeSchema.prefault({}).describe("Home Screen"),
	imeiScan: imeiScanSchema.prefault({}).describe("IMEI Scan"),
	offer: offerSchema.prefault({}).describe("Offer Screen"),
	imageCapture: imageCaptureSchema.prefault({}).describe("Image Capture Page"),
	instructions: instructionsSchema.prefault({}).describe("Instructions Screen"),
	userInfo: userInfoSchema.prefault({}).describe("User Info Screen"),
});

// ---------------------------------------------------------------------------
// Promos
// ---------------------------------------------------------------------------

const promosSchema = z.object({
	basePricePlacement: z
		.enum(["first", "last"])
		.default("last")
		.check(
			fieldConfig({
				fieldType: "singleselect",
				description: "Select whether the base price appears as the first or last item in the offer carousel.",
			}),
		)
		.describe("Base Price Position"),
	isSelectable: z.boolean().default(false).describe("Allow Promo Selection"),
	validationCodeType: z
		.enum(["promo", "EAN"])
		.default("promo")
		.check(
			fieldConfig({
				fieldType: "singleselect",
				description: "Define how the promo is validated (via Promo or EAN)",
			}),
		)
		.describe("Promo Validation Type"),
});

// ---------------------------------------------------------------------------
// Authentication
// ---------------------------------------------------------------------------

// Auth0 connections available for partner login. Mirrors the host→connection
// values in vision-ai's DOMAIN_CONNECTION_MAP (apps/vision-ai/src/utils/auth0.ts);
// add new connections here and there together.
const AUTH0_CONNECTIONS = ["Carrefour"] as const;

const authenticationSchema = z.object({
	requiresAuthentication: z
		.boolean()
		.default(false)
		.check(
			fieldConfig({
				description:
					"When enabled, visitors must authenticate via Auth0 before accessing this partner, and the authenticated user must belong to this partner.",
			}),
		)
		.describe("Requires Authentication"),
	connection: z
		.enum(AUTH0_CONNECTIONS)
		.optional()
		.check(
			fieldConfig({
				fieldType: "singleselect",
				description:
					"Auth0 connection used to log this partner's users in. Leave empty to use the default connection for the current host.",
			}),
		)
		.describe("Auth0 Connection"),
});

// ---------------------------------------------------------------------------
// Vision Schema
// ---------------------------------------------------------------------------

export const visionShape = {
	pagesConfig: pagesConfigSchema.prefault({}).describe("Pages Config"),
	promos: promosSchema.prefault({}).describe("Promos"),
	authentication: authenticationSchema.prefault({}).describe("Authentication"),
	functionalAssessmentExecution: z
		.enum(["external", "internal"])
		.default("external")
		.check(
			fieldConfig({
				fieldType: "singleselect",
				description:
					"Controls whether functional assessments run in the current app or redirect to the webapp (external).",
			}),
		)
		.describe("Functional Assessments Execution"),
};

export const visionSchema = z.object(visionShape);

export type VisionConfig = z.infer<typeof visionSchema>;
